import { useCallback, useEffect, useState } from 'react';
import StatusBadge, { type BadgeTone } from '../StatusBadge';
import Modal from '../Modal';
import { ClientRecordsRows } from './ClientRecordsControls';
import complianceService from '../../services/compliance.service';
import sarsService from '../../services/sars.service';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors, gradients } from '../../styles/theme';
import type { ComplianceState, SuspensionReason } from '../../types/compliance';

/**
 * Client compliance controls (manager / super_admin only) — the staff side of
 * the freeze / suspension / risk-band actions the AML and sanctions queues hand
 * off to. Self-gates by role, so ClientDetail can mount it unconditionally.
 *
 *  · Freeze     — single-actor; blocks money-movement approval.
 *  · Suspension — single-actor to place; REACTIVATION is four-eyes (a second
 *                 manager approves, and a fresh sanctions screen must clear).
 *  · Risk band  — reassess; a DOWNGRADE is four-eyes (proposal → second approver).
 *  · Records    — death notice (record / four-eyes release) and the audited data exports
 *                 (see ClientRecordsControls).
 */

const BAND_TONE: Record<string, BadgeTone> = { high: 'danger', medium: 'warning', low: 'info' };

const SUSPENSION_REASONS: { value: SuspensionReason; label: string }[] = [
  { value: 'manual_compliance', label: 'Manual compliance hold' },
  { value: 'fraud_investigation', label: 'Fraud investigation' },
  { value: 'court_order', label: 'Court order' },
  { value: 'sanctions_hit', label: 'Sanctions hit' },
];

/** Which action the modal performs. Each maps to one service call. */
type ActionKind =
  | 'freeze' | 'unfreeze'
  | 'suspend' | 'reactivation_propose' | 'reactivation_approve'
  | 'reassess' | 'downgrade_approve' | 'downgrade_reject';

const ACTION_COPY: Record<ActionKind, { title: string; blurb: string; cta: string; danger?: boolean }> = {
  freeze: {
    title: 'Freeze account',
    blurb: 'Blocks deposit and withdrawal approval until cleared. Reversible.',
    cta: 'Freeze', danger: true,
  },
  unfreeze: {
    title: 'Remove freeze',
    blurb: 'Restores money movement for this client.',
    cta: 'Unfreeze',
  },
  suspend: {
    title: 'Suspend account',
    blurb: 'Places a compliance hold — the client keeps read-only access but every mutating action is blocked. Reactivation later needs a second manager.',
    cta: 'Suspend', danger: true,
  },
  reactivation_propose: {
    title: 'Propose reactivation',
    blurb: 'Four-eyes step 1: records your proposal to lift the hold. A DIFFERENT manager must approve it, and a fresh sanctions screen must clear.',
    cta: 'Propose reactivation',
  },
  reactivation_approve: {
    title: 'Approve reactivation',
    blurb: 'Four-eyes step 2: lifts the hold. You cannot approve a reactivation you proposed; a fresh sanctions screen runs first and a hit keeps the hold.',
    cta: 'Approve reactivation',
  },
  reassess: {
    title: 'Reassess AML risk',
    blurb: 'Recomputes the risk band from current facts. An upgrade applies at once; a DOWNGRADE is recorded as a proposal a second manager must approve.',
    cta: 'Reassess',
  },
  downgrade_approve: {
    title: 'Approve risk downgrade',
    blurb: 'Four-eyes: confirms the lower band. You cannot approve a downgrade you proposed.',
    cta: 'Approve downgrade',
  },
  downgrade_reject: {
    title: 'Reject risk downgrade',
    blurb: 'The band stays where it is.',
    cta: 'Reject downgrade',
  },
};

interface RowProps {
  label: string;
  badge: JSX.Element;
  detail?: string | null;
  children: React.ReactNode;
}

function ControlRow({ label, badge, detail, children }: Readonly<RowProps>): JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-3 py-3"
      style={{ borderTop: `1px solid ${colors.borderSoft}` }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: colors.ink }}>{label}</span>
          {badge}
        </div>
        {detail && (
          <div className="text-xs mt-0.5 line-clamp-1" style={{ color: colors.muted }}>{detail}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">{children}</div>
    </div>
  );
}

function actionButton(
  label: string, onClick: () => void, danger = false,
): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-md text-xs font-semibold"
      style={
        danger
          ? { background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }
          : { border: `1px solid ${colors.border}`, color: colors.inkSoft }
      }
    >
      {label}
    </button>
  );
}

interface PanelBodyProps {
  state: ComplianceState;
  onAction: (a: ActionKind) => void;
  onDraftSar: () => void;
}

/** The rendered rows. Extracted so the container's render stays flat
 *  (Sonar S3776) — one row per compliance lever. */
function PanelBody({ state, onAction, onDraftSar }: Readonly<PanelBodyProps>): JSX.Element {
  const frozen = state.restriction_status === 'frozen';
  const suspended = state.suspension_status === 'suspended';
  const proposed = state.reactivation_proposed;

  const suspensionDetail = suspended
    ? [state.suspension_reason, proposed ? 'reactivation proposed' : null]
        .filter(Boolean).join(' · ')
    : 'Full portal access';

  return (
    <>
      <ControlRow
        label="Freeze"
        badge={<StatusBadge tone={frozen ? 'warning' : 'success'}>{frozen ? 'Frozen' : 'Active'}</StatusBadge>}
        detail={frozen ? state.restriction_reason : 'Money movement allowed'}
      >
        {frozen
          ? actionButton('Unfreeze', () => onAction('unfreeze'))
          : actionButton('Freeze', () => onAction('freeze'), true)}
      </ControlRow>

      <ControlRow
        label="Suspension"
        badge={<StatusBadge tone={suspended ? 'danger' : 'success'}>{suspended ? 'Suspended' : 'Active'}</StatusBadge>}
        detail={suspensionDetail}
      >
        {!suspended && actionButton('Suspend', () => onAction('suspend'), true)}
        {suspended && !proposed &&
          actionButton('Propose reactivation', () => onAction('reactivation_propose'))}
        {suspended && proposed &&
          actionButton('Approve reactivation', () => onAction('reactivation_approve'))}
      </ControlRow>

      <ControlRow
        label="AML risk band"
        badge={
          <StatusBadge tone={BAND_TONE[state.risk_band ?? ''] ?? 'info'}>
            {state.risk_band ?? 'unrated'}
          </StatusBadge>
        }
        detail={
          state.open_downgrade
            ? `Downgrade proposed: ${state.open_downgrade.from_band} → ${state.open_downgrade.to_band}`
            : null
        }
      >
        {state.open_downgrade ? (
          <>
            {actionButton('Approve', () => onAction('downgrade_approve'))}
            {actionButton('Reject', () => onAction('downgrade_reject'))}
          </>
        ) : (
          actionButton('Reassess', () => onAction('reassess'))
        )}
      </ControlRow>

      <ControlRow
        label="Suspicious Activity Report"
        badge={<span />}
        detail="Draft a SAR from this client's signals; a second manager files it under Compliance → SARs."
      >
        {actionButton('Draft SAR', onDraftSar)}
      </ControlRow>
    </>
  );
}

export default function ClientComplianceActions(
  { clientId }: Readonly<{ clientId: string }>,
): JSX.Element | null {
  const profile = useCurrentUserProfile();
  const [state, setState] = useState<ComplianceState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<ActionKind | null>(null);
  const [sarOpen, setSarOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setState(await complianceService.getState(clientId));
      setError(null);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load compliance state.'));
    }
  }, [clientId]);

  const isManager = profile?.role === 'manager' || profile?.role === 'super_admin';
  useEffect(() => { if (isManager) reload(); }, [isManager, reload]);

  if (!isManager) return null;

  return (
    <div
      className="rounded-2xl p-4 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="text-sm font-semibold mb-1" style={{ color: colors.ink }}>Compliance controls</div>
      {flash && (
        <div className="mb-2 text-xs" style={{ color: colors.successText }}>{flash}</div>
      )}
      {error && (
        <div className="mb-2 text-xs" style={{ color: colors.danger }}>{error}</div>
      )}
      {state ? (
        <PanelBody
          state={state}
          onAction={setAction}
          onDraftSar={() => setSarOpen(true)}
        />
      ) : (
        !error && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      )}

      {(state || error) && (
        <ClientRecordsRows
          clientId={clientId}
          role={profile?.role}
          viewerId={profile?.id}
          state={state}
          onDone={(msg) => {
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 6000);
            reload();
          }}
        />
      )}

      {action && state && (
        <ActionModal
          clientId={clientId}
          action={action}
          state={state}
          onClose={() => setAction(null)}
          onDone={(msg) => {
            setAction(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 6000);
            reload();
          }}
        />
      )}

      {sarOpen && (
        <SarDraftModal
          clientId={clientId}
          onClose={() => setSarOpen(false)}
          onDone={(msg) => {
            setSarOpen(false);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 7000);
          }}
        />
      )}
    </div>
  );
}

// ─── Draft SAR dialog ─────────────────────────────────────────────
interface SarDraftModalProps {
  clientId: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}

function SarDraftModal({ clientId, onClose, onDone }: Readonly<SarDraftModalProps>): JSX.Element {
  const [reason, setReason] = useState('');
  const [narrative, setNarrative] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const valid = reason.trim().length >= 1;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      await sarsService.draft(clientId, reason.trim(), narrative.trim() || undefined);
      onDone('SAR drafted — a second manager files it under Compliance → SARs.');
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not draft the SAR.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Draft SAR</h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Assembles a regulator-ready report from this client&apos;s risk band, monitoring
        alerts, sanctions hits and recent activity. A DIFFERENT manager files it.
      </p>
      <label htmlFor="sar-draft-reason" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
        Reason
      </label>
      <input
        id="sar-draft-reason"
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3"
        style={{ border: `1px solid ${colors.border}` }}
      />
      <label htmlFor="sar-draft-narrative" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
        Narrative (optional)
      </label>
      <textarea
        id="sar-draft-narrative"
        value={narrative}
        onChange={(e) => setNarrative(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 rounded-lg text-sm mb-4"
        style={{ border: `1px solid ${colors.border}` }}
      />
      {err && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button" onClick={onClose} disabled={busy}
          className="px-4 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Cancel
        </button>
        <button
          type="button" onClick={submit} disabled={!valid || busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: gradients.gold, color: colors.navy, opacity: valid && !busy ? 1 : 0.5 }}
        >
          {busy ? 'Saving…' : 'Draft SAR'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Action dialog ────────────────────────────────────────────────
interface ActionModalProps {
  clientId: string;
  action: ActionKind;
  state: ComplianceState;
  onClose: () => void;
  onDone: (msg: string) => void;
}

async function runAction(
  action: ActionKind, clientId: string, state: ComplianceState, text: string,
  suspendReason: SuspensionReason,
): Promise<string> {
  const svc = complianceService;
  switch (action) {
    case 'freeze': await svc.freeze(clientId, text); return 'Account frozen.';
    case 'unfreeze': await svc.unfreeze(clientId, text); return 'Freeze removed.';
    case 'suspend': await svc.suspend(clientId, suspendReason, text); return 'Account suspended.';
    case 'reactivation_propose':
      await svc.proposeReactivation(clientId, text);
      return 'Reactivation proposed — a second manager must approve.';
    case 'reactivation_approve':
      await svc.approveReactivation(clientId, text); return 'Account reactivated.';
    case 'reassess': {
      const r = await svc.reassess(clientId, text);
      return r.status === 'pending_second_approval'
        ? `Downgrade to ${r.to_band} proposed — a second manager must approve.`
        : 'Risk band reassessed.';
    }
    case 'downgrade_approve':
    case 'downgrade_reject': {
      const dgId = state.open_downgrade?.id ?? '';
      if (action === 'downgrade_approve') {
        await svc.approveDowngrade(clientId, dgId, text);
        return 'Downgrade approved.';
      }
      await svc.rejectDowngrade(clientId, dgId, text);
      return 'Downgrade rejected.';
    }
  }
}

function ActionModal({ clientId, action, state, onClose, onDone }: Readonly<ActionModalProps>): JSX.Element {
  const [text, setText] = useState('');
  const [suspendReason, setSuspendReason] = useState<SuspensionReason>('manual_compliance');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const copy = ACTION_COPY[action];
  const valid = text.trim().length >= 1;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      onDone(await runAction(action, clientId, state, text.trim(), suspendReason));
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not complete the action.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>{copy.title}</h2>
      <p className="text-sm mb-4" style={{ color: copy.danger ? colors.danger : colors.muted }}>{copy.blurb}</p>

      {action === 'suspend' && (
        <div className="mb-3">
          <label htmlFor="susp-reason" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
            Reason
          </label>
          <select
            id="susp-reason"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value as SuspensionReason)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            {SUSPENSION_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      )}

      <label htmlFor="action-note" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
        {action === 'suspend' ? 'Note (optional context)' : 'Reason (kept in the audit log)'}
      </label>
      <textarea
        id="action-note"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 rounded-lg text-sm mb-4"
        style={{ border: `1px solid ${colors.border}` }}
      />

      {err && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button" onClick={onClose} disabled={busy}
          className="px-4 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Cancel
        </button>
        <button
          type="button" onClick={submit} disabled={!valid || busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: copy.danger ? colors.danger : gradients.gold,
            color: copy.danger ? '#fff' : colors.navy,
            opacity: valid && !busy ? 1 : 0.5,
          }}
        >
          {busy ? 'Saving…' : copy.cta}
        </button>
      </div>
    </Modal>
  );
}
